/**
 * ConfigRealtimePage
 *
 * Admin page to configure the realtime event bus for this org.
 * Reads/writes organisation/{org_id}/config/realtime in Firestore via
 * the /admin/config/realtime endpoints.
 *
 * Route: /settings/config/realtime  (admin-only)
 */

import { useCallback, useEffect, useState } from "react";
import useFeedback from "../../hooks/useFeedback";
import SettingsPageShell from "../../components/Settings/SettingsPageShell";
import { PrimaryButton } from "../../components/Button/Buttons";
import Input from "../../components/Inputs/Input";
import Select from "../../components/Inputs/Select";
import Loader from "../../components/Loader/Loader";
import { getServiceConfig, updateServiceConfig } from "../../api/adminConfig";
import { AuthError } from "../../api";
import UIStore from "../../store/uiStore";
import { isDirty } from "../../utils/isDirty";
import { EVENT_BUS_OPTIONS } from "./configAdapterOptions";

const EMPTY_FORM = {
  event_bus_adapter: "pubsub",
  pubsub_topic:      "",
};

const ConfigRealtimePage = () => {
  const { showFeedback } = useFeedback();
  const orgAvailable = UIStore((s) => s.orgAvailable);

  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [form, setForm]           = useState(EMPTY_FORM);
  const [originalForm, setOriginalForm] = useState(EMPTY_FORM);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getServiceConfig("realtime");
      if (data.configured) {
        const loaded = {
          event_bus_adapter: data.event_bus_adapter ?? "pubsub",
          pubsub_topic:      data.pubsub_topic      ?? "",
        };
        setForm(loaded);
        setOriginalForm(loaded);
      } else {
        setForm(EMPTY_FORM);
        setOriginalForm(EMPTY_FORM);
      }
    } catch (err) {
      if (!(err instanceof AuthError)) showFeedback("Failed to load realtime config.", "error");
    } finally {
      setLoading(false);
    }
  }, [showFeedback]);

  useEffect(() => {
    if (orgAvailable) fetchConfig();
  }, [orgAvailable, fetchConfig]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateServiceConfig("realtime", form);
      setOriginalForm(form);
      showFeedback("Realtime config saved.", "success");
    } catch (err) {
      showFeedback(err?.response?.data?.error || "Failed to save realtime config.", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingsPageShell
      title="Realtime Configuration (SSE)"
      description="Configure the event bus used for realtime agent and tool updates."
    >
      {loading ? (
        <div className="flex justify-center py-16">
          <Loader />
        </div>
      ) : (
        <div className="flex flex-col gap-6 max-w-lg">
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">
              Event Bus Adapter
            </label>
            <Select
              value={form.event_bus_adapter}
              onChange={(adapterType) => setForm((prev) => ({
                ...prev,
                event_bus_adapter: adapterType,
                pubsub_topic: adapterType === "local" ? "" : prev.pubsub_topic,
              }))}
              options={EVENT_BUS_OPTIONS}
            />
            <p className="text-[11px] text-ink-gray-400 dark:text-ink-gray-500">
              Pub/Sub provides cross-instance delivery. Local routes events only inside this API process. Adapter changes require all API instances to restart.
            </p>
          </div>

          {form.event_bus_adapter === "pubsub" && (
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">
                Pub/Sub Topic
              </label>
              <Input
                type="text"
                placeholder="photon-realtime-events"
                value={form.pubsub_topic}
                onChange={(e) => setForm((prev) => ({ ...prev, pubsub_topic: e.target.value }))}
              />
              <p className="text-[11px] text-ink-gray-400 dark:text-ink-gray-500">
                Topic name (without project path) used for realtime event fan-out. Must exist in your GCP project before deploying.
              </p>
            </div>
          )}

          <div className="flex items-center gap-3 pt-2">
            <PrimaryButton onClick={handleSave} disabled={saving || !isDirty(form, originalForm)}>
              {saving ? "Saving…" : "Save"}
            </PrimaryButton>
          </div>
        </div>
      )}
    </SettingsPageShell>
  );
};

export default ConfigRealtimePage;
